import { useStorePageSnapshot } from "./use-store-page-snapshot";
import { useReducer, useRef } from "react";

import type { StorePageEditConflict } from "../store-page/types";

import { draftReducer, initialDraftState } from "./draft-state";
import { presentDraft } from "./present-draft";
import type { Acknowledgment, ContentPatch, LastAction } from "./draft-model";

export function useStorePageDraft({
  slug,
  enabled,
}: {
  readonly slug: string;
  readonly enabled: boolean;
}) {
  const { query, cancelRead, refetchActive, acceptConflict, acceptRevision } =
    useStorePageSnapshot({ slug, enabled });
  const [state, dispatch] = useReducer(draftReducer, initialDraftState);
  const contentVersion = useRef(0);
  const { initialDraft, ...presentation } = presentDraft({
    state,
    query,
    enabled,
  });

  function updateContent(patch: ContentPatch) {
    contentVersion.current += 1;
    dispatch({
      type: "edit",
      patch,
      initial: initialDraft,
    });
  }

  function discardChanges() {
    contentVersion.current += 1;
    dispatch({ type: "discard" });
  }

  async function applyConflict(conflict: StorePageEditConflict) {
    const initial = await acceptConflict(conflict);
    dispatch({ type: "conflict", conflict, initial });
  }

  async function applySavedDraft({
    result,
    submittedContentVersion,
  }: Acknowledgment) {
    await acceptRevision(result);
    dispatch({
      type: "saved",
      result,
      editedSinceSubmit: contentVersion.current !== submittedContentVersion,
    });
  }

  async function applyRevision({
    operation,
    result,
    submittedContentVersion,
  }: Acknowledgment & {
    readonly operation: "approve" | "reject" | "revert";
  }) {
    await acceptRevision(result);
    dispatch({
      type: "revision",
      operation,
      editedSinceSubmit: contentVersion.current !== submittedContentVersion,
    });
  }

  function resolveConflictKeepMine() {
    if (!state.conflict) return;

    dispatch({
      type: "keep-mine",
      conflict: state.conflict,
      savedContent: initialDraft.savedContent,
    });
    void refetchActive();
  }

  function resolveConflictTakeTheirs() {
    if (!state.conflict) return;

    discardChanges();
    void refetchActive();
  }

  async function refresh({ throwOnError }: { readonly throwOnError: boolean }) {
    const result = await query.refetch({ throwOnError });

    if (result.isSuccess) dispatch({ type: "refreshed" });
  }

  return {
    ...presentation,
    contentVersion,
    cancelRead,
    refresh: () => refresh({ throwOnError: false }),
    refreshAfterWrite: () => refresh({ throwOnError: true }),
    updateContent,
    discardChanges,
    applyConflict,
    applySavedDraft,
    applyRevision,
    setLastAction: (action: LastAction) =>
      dispatch({ type: "last-action", action }),
    resolveConflictKeepMine,
    resolveConflictTakeTheirs,
  };
}
